import { useCallback, useEffect, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import { PAYMENT_METHODS } from "@sakani/contracts";
import { ApiError, api, savedBookings, shrinkImage, uploadProof, type BookingDetail } from "../api.ts";
import { money } from "../format.ts";
import { useI18n } from "../i18n.tsx";
import { IconArrow } from "../ui/Icons.tsx";
import { Pattern } from "../ui/Pattern.tsx";

/** How far along the managed flow a status is: request → availability → payment → keys. */
const PROGRESS: Record<string, number> = { requested: 1, awaiting_payment: 2, confirmed: 3, in_stay: 4, completed: 4 };
const DEAD = ["declined", "expired", "cancelled_by_guest", "cancelled_by_host"];

export function BookingPage() {
  const { id = "" } = useParams();
  const { hash } = useLocation();
  const { t, locale } = useI18n();
  // The link carries the token in its fragment; a returning guest on the same device falls back to what we saved.
  const token = hash.slice(1) || savedBookings().find((b) => b.id === id)?.token || "";
  const [d, setD] = useState<BookingDetail | null>(null);
  const [state, setState] = useState<"loading" | "ok" | "invalid" | "error">("loading");
  const [pay, setPay] = useState({ method: "bankily", reference: "" });
  const [sent, setSent] = useState(false);
  const [shot, setShot] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const [payErr, setPayErr] = useState<string | null>(null);
  useEffect(() => {
    if (!shot) { setPreview(null); return; }
    const u = URL.createObjectURL(shot);
    setPreview(u);
    return () => URL.revokeObjectURL(u);
  }, [shot]);

  const load = useCallback(async () => {
    try { setD(await api<BookingDetail>(`/guest/bookings/${id}`, { token })); setState("ok"); }
    catch (e) { setState((e as { status?: number }).status === 401 ? "invalid" : "error"); }
  }, [id, token]);
  useEffect(() => { void load(); }, [load]);
  // Staff answer in minutes to hours: refresh while the guest keeps the page open.
  useEffect(() => { const i = setInterval(() => void load(), 30_000); return () => clearInterval(i); }, [load]);

  if (state === "loading") return <div className="wrap section"><div className="skel" style={{ aspectRatio: "16/7" }} aria-hidden="true" /></div>;
  if (state !== "ok" || !d) return <div className="wrap section"><div className="note"><Pattern /><p className="h2">{state === "invalid" ? t("link.invalid") : t("error")}</p><Link className="link" to="/">{t("back")}</Link></div></div>;

  const b = d.booking;
  const step = PROGRESS[b.status] ?? 0;
  const dead = DEAD.includes(b.status);
  const due = d.payments.find((p) => p.kind === "deposit" && p.status === "due");
  const cancellable = ["requested", "awaiting_payment", "confirmed"].includes(b.status);

  async function submitProof(e: React.FormEvent) {
    e.preventDefault();
    if (!due || (!pay.reference.trim() && !shot)) return;
    setSending(true); setPayErr(null);
    try {
      const proofKey = shot ? await uploadProof(id, token, await shrinkImage(shot)) : undefined;
      await api(`/guest/bookings/${id}/payments/${due.id}/submit`, { token, body: { method: pay.method, reference: pay.reference.trim() || undefined, proofKey } });
      setSent(true);
      void load();
    } catch (err) {
      const k = err instanceof ApiError ? `err.${err.code}` : "";
      setPayErr(k && t(k) !== k ? t(k) : t("error"));
    } finally { setSending(false); }
  }
  async function cancel() {
    await api(`/guest/bookings/${id}/cancel`, { token, method: "POST", body: {} });
    void load();
  }

  return (
    <div className="wrap section">
      <span className="eyebrow">{t("b.title")} · <span className="num">{b.code}</span></span>
      <h1 className="h1" style={{ margin: "12px 0 24px" }}>{t(`status.${b.status}`)}</h1>
      <span className={`status-line ${dead || step >= 3 ? "done" : ""}`}><i /><span className="num" dir="ltr">{b.checkIn} → {b.checkOut}</span></span>

      {!dead && (
        <ol className="timeline" aria-label="progress">
          {[1, 2, 3, 4].map((n) => <li key={n} className={n < step ? "done" : n === step ? "now" : ""}>{t(`tl.${n}`)}</li>)}
        </ol>
      )}

      <div className="stack" style={{ marginBlockStart: dead ? 40 : 0 }}>
        <div>
          <div className="kv"><span className="muted">{t("b.dates")}</span><span className="num" dir="ltr">{b.nights} · {b.checkIn} → {b.checkOut}</span></div>
          <div className="kv"><span className="muted">{t("b.total")}</span><b className="num" style={{ fontWeight: 400 }}>{money(b.totalMinor, b.currency, locale)}</b></div>
        </div>

        {due && b.status === "awaiting_payment" && (
          <form className="panel" onSubmit={submitProof}>
            <div className="price-line"><span className="eyebrow" style={{ flex: 1 }}>{t("b.pay")}</span><b className="num">{money(due.amountMinor, b.currency, locale)}</b></div>
            <label className="field"><span>{t("b.method")}</span>
              <select value={pay.method} onChange={(e) => setPay({ ...pay, method: e.target.value })}>
                {PAYMENT_METHODS.filter((m) => m !== "card").map((m) => <option key={m} value={m}>{t(`m.${m}`)}</option>)}
              </select>
            </label>
            <p className="muted small" style={{ margin: 0 }}>{t("b.proofRule")}</p>
            <label className="field"><span>{t("b.reference")}</span><input autoComplete="off" value={pay.reference} onChange={(e) => setPay({ ...pay, reference: e.target.value })} /></label>
            <div className="or"><span className="eyebrow">{t("b.or")}</span></div>
            {preview ? (
              <div className="shot">
                <img src={preview} alt="" />
                <div><b className="small">{t("b.attached")}</b><button type="button" className="link small" onClick={() => setShot(null)}>{t("b.remove")}</button></div>
              </div>
            ) : (
              <label className="attach"><input type="file" accept="image/jpeg,image/png,image/webp" onChange={(e) => setShot(e.target.files?.[0] ?? null)} />{t("b.attach")}</label>
            )}
            {payErr && <p className="err" role="alert">{payErr}</p>}
            <button className="btn" type="submit" disabled={sending || (!pay.reference.trim() && !shot)}>{sending ? t("req.sending") : t("b.sendProof")} <IconArrow /></button>
            {sent && <p className="muted small" role="status">{t("b.proofSent")}</p>}
          </form>
        )}

        <p className="muted small">{t("b.save")}</p>
        {cancellable && <button className="btn ghost" style={{ justifySelf: "start" }} onClick={cancel}>{t("b.cancel")}</button>}
      </div>
    </div>
  );
}
